import { lazy, Suspense } from 'react';
import type { JSX } from 'react';
import { Route, Routes } from 'react-router-dom';

import Loading from '~/components/molecules/_shared/Loading';
import IAllowedSystemEnum from '~/enums/allowedSystem.enum';
import Logoff from './Logoff';
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';

// Lazy loading dos componentes
const Login = lazy(() => import('~/pages/LoginPage'));
const Register = lazy(() => import('~/pages/RegisterPage'));
const RememberPassword = lazy(() => import('~/pages/RememberPasswordPage'));
const EmailConfirmation = lazy(() => import('~/pages/EmailConfirmationPage'));
const ConfirmRegister = lazy(() => import('~/pages/ConfirmRegisterPage'));
const Apps = lazy(() => import('~/pages/AppsPage'));
const Profile = lazy(() => import('~/pages/ProfilePage'));
const Koa = lazy(() => import('~/pages/KoaPage'));
const UserManagement = lazy(() => import('~/pages/UserManagementPage'));
const UserPermissions = lazy(() => import('~/pages/UserPermissionsPage'));
const VeilingPermissions = lazy(() => import('~/pages/VeilingPermissionsPage'));
const ChangePassword = lazy(() => import('~/pages/ChangePasswordPage'));
const ExpiredUserPassword = lazy(() => import('~/pages/ExpiredUserPasswordPage'));
const Maintenance = lazy(() => import('~/pages/MaintenancePage'));
const Notifications = lazy(() => import('~/pages/NotificationsPage'));
const Devices = lazy(() => import('~/pages/DevicesPage'));
const DeviceDetail = lazy(() => import('~/pages/DeviceDetailPage'));
const AuthCallback = lazy(() => import('~/pages/AuthCallbackPage'));
const AccountAccessManagement = lazy(() => import('~/pages/AccountAccessManagementPage'));
const LinkAccount = lazy(() => import('~/pages/LinkAccountPage'));
const AuthError = lazy(() => import('~/pages/AuthErrorPage'));

export default function AppRoutes(): JSX.Element {
  return (
    <Suspense fallback={<Loading loading />}>
      <Routes>
        {/* Rota pública - Login */}
        <Route
          path="/"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* Logoff */}
        <Route path="/logoff" element={<Logoff />} />

        {/* Rotas de auth social */}
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/vincular-conta" element={<LinkAccount />} />
        <Route path="/auth/erro" element={<AuthError />} />

        {/* Rotas públicas */}
        <Route path="/registrar" element={<Register />} />
        <Route path="/esqueci-minha-senha" element={<RememberPassword />} />
        <Route path="/email-confirmacao" element={<EmailConfirmation />} />
        <Route path="/user/confirm-register/" element={<ConfirmRegister />} />
        <Route path="/password/change/" element={<ChangePassword />} />
        <Route path="/senha-expirada/" element={<ExpiredUserPassword />} />
        <Route path="/maintenance" element={<Maintenance />} />

        {/* Rotas protegidas */}
        <Route
          path="/apps"
          element={
            <ProtectedRoute>
              <Apps />
            </ProtectedRoute>
          }
        />
        <Route
          path="/meu-perfil"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notificacoes"
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dispositivos"
          element={
            <ProtectedRoute>
              <Devices />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dispositivos/:id"
          element={
            <ProtectedRoute>
              <DeviceDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/koa/"
          element={
            <ProtectedRoute>
              <Koa />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestao"
          element={
            <ProtectedRoute allowedSystem={IAllowedSystemEnum.UserManagement}>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestao/:userId"
          element={
            <ProtectedRoute allowedSystem={IAllowedSystemEnum.UserManagement}>
              <UserPermissions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestao/:userId/veiling"
          element={
            <ProtectedRoute allowedSystem={IAllowedSystemEnum.UserManagement}>
              <VeilingPermissions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account-access-management"
          element={
            <ProtectedRoute>
              <AccountAccessManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account-access-management"
          element={
            <ProtectedRoute>
              <AccountAccessManagement />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}
